import React, { useEffect, useMemo, useState } from "react";

import { Row, Col } from "antd";
import axios from "axios";
import { Form, Formik, FormikHelpers } from "formik";

import * as Yup from "yup";

import { mergeEmployeeIntoEmployeesCaches } from "manager/hooks/api/employeeQueries";
import { Employee } from "manager/interfaces/api/employee";
import { Input, VeroFormField } from "shared/components/Form";
import { Modal } from "shared/components/Modal";
import Spacer from "shared/components/Spacer";
import { SmallErrorText, WeakText } from "shared/components/Typography";
import { HTTP_ERROR_CODES, NOTIFICATIONS } from "shared/config/constants";
import useModal from "shared/hooks/useModal";
import { resendEmployeeInvite } from "shared/lib/api/employeeApi";
import { logError } from "shared/lib/errors";
import { toCamelCaseKeys } from "shared/utils/misc.util";
import { openNotification } from "shared/utils/ui";

const FIELD_EMAIL = "email";

const RESEND_COOLDOWN_MS = 5 * 60 * 1000;

const ERROR_CODES = {
  emailChangeMultiCompany: "email_change_multi_company",
  emailChangeInviterNotAllowed: "email_change_inviter_not_allowed",
  invitationAlreadyAccepted: "invitation_already_accepted",
  cannotResendInvitation: "cannot_resend_invitation",
} as const;

const emailSchema = Yup.object({
  [FIELD_EMAIL]: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Required"),
});

const emailsMatchForResend = (a: string, b: string) => a.trim() === b.trim();

const unwrapErrorPayload = (payload: unknown): Record<string, unknown> => {
  if (
    payload !== null &&
    typeof payload === "object" &&
    !Array.isArray(payload)
  ) {
    return toCamelCaseKeys(payload as Record<string, unknown>) as Record<
      string,
      unknown
    >;
  }
  return {};
};

const detailToString = (detail: unknown): string => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (
          item &&
          typeof item === "object" &&
          typeof (item as { detail?: unknown }).detail === "string"
        ) {
          return (item as { detail: string }).detail;
        }
        return "";
      })
      .filter(Boolean)
      .join(" ");
  }
  return "";
};

const primaryEmailViolation = (payload: Record<string, unknown>): string => {
  const emails = payload.email;
  if (Array.isArray(emails) && typeof emails[0] === "string") {
    return emails[0];
  }
  if (typeof emails === "string") return emails;
  return "";
};

export interface ResendEmployeeInviteModalProps {
  employee: Employee;
}

const ResendEmployeeInviteModal = ({
  employee,
}: ResendEmployeeInviteModalProps) => {
  const {
    selfApi: { closeDialog: closeThisModal },
  } = useModal();

  const initialEmail = employee.user?.email ?? "";
  const employeeFullName = useMemo(
    () =>
      `${employee.user?.firstName ?? ""} ${
        employee.user?.lastName ?? ""
      }`.trim(),
    [employee.user?.firstName, employee.user?.lastName]
  );

  const [restrictToCurrentEmailOnly, setRestrictToCurrentEmailOnly] =
    useState(false);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  /** Bumps once per second while cooldown runs so countdown labels repaint. */
  const [cooldownTick, setCooldownTick] = useState(0);

  useEffect(() => {
    setRestrictToCurrentEmailOnly(false);
    setCooldownEndsAt(null);
  }, [employee.id]);

  useEffect(() => {
    if (!cooldownEndsAt) return undefined;

    const id = window.setInterval(() => {
      setCooldownTick((t) => t + 1);
      if (Date.now() >= cooldownEndsAt) {
        window.clearInterval(id);
        setCooldownEndsAt(null);
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [cooldownEndsAt]);

  const cooldownSecondsLeft = useMemo(
    () =>
      cooldownEndsAt !== null
        ? Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000))
        : 0,
    [cooldownEndsAt, cooldownTick]
  );
  const cooldownActive = cooldownEndsAt !== null && cooldownSecondsLeft > 0;

  const cooldownLabelSuffix = cooldownActive
    ? ` (${String(Math.floor(cooldownSecondsLeft / 60)).padStart(
        2,
        "0"
      )}:${String(cooldownSecondsLeft % 60).padStart(2, "0")})`
    : "";

  const submit = async (
    values: { email: string },
    helpers: FormikHelpers<{ email: string }>
  ) => {
    const { setFieldError, setFieldValue, setStatus, setSubmitting } = helpers;
    setStatus(undefined);

    const trimmed = values.email.trim();
    const body =
      restrictToCurrentEmailOnly || emailsMatchForResend(trimmed, initialEmail)
        ? {}
        : { email: trimmed };

    setSubmitting(true);
    try {
      const updated = await resendEmployeeInvite(employee.id, body);
      const sentTo = updated.user?.email ?? trimmed;

      mergeEmployeeIntoEmployeesCaches(updated);

      closeThisModal();
      openNotification(`Invite resent to ${sentTo}`, NOTIFICATIONS.info);
    } catch (err) {
      if (!axios.isAxiosError(err) || !err.response) {
        logError(err);
        setStatus({
          formError: "We couldn't resend the invite. Please try again.",
        });
      } else {
        const { status } = err.response;
        const data = unwrapErrorPayload(err.response.data);
        const code = typeof data.code === "string" ? data.code : undefined;
        const detailRaw = detailToString(data.detail);

        if (status === HTTP_ERROR_CODES.conflict) {
          setFieldError(
            FIELD_EMAIL,
            primaryEmailViolation(data) || "This email is already in use."
          );
        } else if (status === HTTP_ERROR_CODES.forbidden) {
          openNotification(
            detailRaw || "You don't have permission to resend this invite.",
            NOTIFICATIONS.error
          );
          closeThisModal();
        } else if (status === 429) {
          setCooldownEndsAt(Date.now() + RESEND_COOLDOWN_MS);
          setStatus({
            formError:
              detailRaw ||
              "Too many attempts. Please wait a few minutes and try again.",
          });
        } else if (status === HTTP_ERROR_CODES.badRequest && code) {
          switch (code) {
            case ERROR_CODES.emailChangeMultiCompany:
              setRestrictToCurrentEmailOnly(true);
              setFieldValue(FIELD_EMAIL, initialEmail);
              setStatus({
                formError:
                  detailRaw || "Email cannot be changed for this coworker.",
              });
              break;
            case ERROR_CODES.emailChangeInviterNotAllowed:
              setFieldError(
                FIELD_EMAIL,
                detailRaw || "Pick a different email address."
              );
              break;
            case ERROR_CODES.invitationAlreadyAccepted:
            case ERROR_CODES.cannotResendInvitation:
              setStatus({
                formError: detailRaw || "Unable to resend this invitation.",
              });
              break;
            default:
              setStatus({
                formError:
                  detailRaw ||
                  `Something went wrong.${code ? ` (${code})` : ""}`,
              });
          }
        } else if (status === HTTP_ERROR_CODES.badRequest) {
          setStatus({
            formError:
              detailRaw || `Something went wrong.${code ? ` (${code})` : ""}`,
          });
        } else if (status >= 500) {
          setStatus({
            formError:
              detailRaw || "We couldn't resend the invite. Please try again.",
          });
        } else {
          setStatus({
            formError:
              detailRaw || "We couldn't resend the invite. Please try again.",
          });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      validationSchema={emailSchema}
      initialValues={{ email: initialEmail }}
      enableReinitialize
      onSubmit={submit}
    >
      {({ submitForm, isSubmitting, isValid, status }) => (
        <Form>
          {/* @ts-ignore */}
          <Modal
            title="Resend invite"
            subtitle={employeeFullName || undefined}
            submitButtonLabel={
              isSubmitting
                ? "Sending..."
                : cooldownActive
                ? `Resend invite${cooldownLabelSuffix}`
                : "Resend invite"
            }
            submit={submitForm}
            closeOnSubmit={false}
            submitButtonDisabled={isSubmitting || !isValid || cooldownActive}
            width="auto"
            style={{ maxWidth: 560 }}
          >
            <Modal.Body>
              {restrictToCurrentEmailOnly && (
                <>
                  <WeakText>
                    You can keep resending to this email. To change their
                    address, use your single-company record or coordinate with
                    support if they belong to more than one company.
                  </WeakText>
                  <Spacer size={Spacer.SIZES.sm} />
                </>
              )}
              {status?.formError && (
                <>
                  <SmallErrorText>{status.formError}</SmallErrorText>
                  <Spacer size={Spacer.SIZES.sm} />
                </>
              )}
              <Row gutter={[20, 20]}>
                <Col md={{ span: 24 }} xs={{ span: 24 }}>
                  {/* @ts-ignore */}
                  <VeroFormField
                    id={FIELD_EMAIL}
                    name={FIELD_EMAIL}
                    data-testid={FIELD_EMAIL}
                    label="Email"
                    type="email"
                    required
                    disabled={restrictToCurrentEmailOnly}
                    as={Input}
                  />
                </Col>
              </Row>
            </Modal.Body>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default ResendEmployeeInviteModal;
